import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { useUpdateUserTreasureTotalMutation } from '@/api/user/userApiSlice';
import { useParams } from 'react-router-dom';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AppNumberTextField from '@/common/components/form-inputs/AppNumberTextField';
import { UserTreasureRecordDetailDTO } from '@/api/DTO/user/userList.interface';
import { showLabel } from '@/utils/helper';
import { BOX_SERIES_TYPE } from '@/common/constants/common';
interface Props {
	details: UserTreasureRecordDetailDTO;
}

const ChangeTreasureTotalModal = (props: Props) => {
	const { details } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [updateUserTreasureTotal] = useUpdateUserTreasureTotalMutation();
	const { id } = useParams();

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		num: Yup.number()
			.nullable()
			.transform((curr, orig) => (orig === '' ? null : curr))
			.max(999999 - Number(details.userTreasureCount), t('errorMessage.max999'))
			.test(
				'not-zero',
				t('errorMessage.pleaseInputQuantity'),
				(value) => !!value
			),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset({});
		setOpen(false);
	};

	const watchUserTreasureCount = methods.watch('num');

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await updateUserTreasureTotal({
			...data,
			userId: id,
			treasureId: details.gameTreasureId,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.changeTreasureTotalSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<>
			<IconButton
				color='primary'
				onClick={() => {
					setOpen(true);
				}}
			>
				<CreateOutlinedIcon />
			</IconButton>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='sm'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>{t('user.changeTreasureNum')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Typography variant='body2' color='common.neutral40'>
								{showLabel(details.gameType, BOX_SERIES_TYPE)} /{' '}
								{details.gameSeriesName} /{' '}
								<span style={{ color: '#F3950D', fontWeight: 'bold' }}>
									{details.gameTreasureName}
								</span>
							</Typography>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12} display='flex' alignItems='center'>
									<Box
										width='100%'
										display='flex'
										gap={1}
										flexDirection='column'
									>
										<Grid container>
											<Grid item width={'120px'} marginRight='16px'>
												<Typography variant='body2' color='common.neutral40'>
													{t('user.oldTreasureNum')}
												</Typography>
											</Grid>
											<Grid item xs>
												<Typography
													variant='body1'
													sx={{ wordBreak: 'break-word' }}
												>
													{details.userTreasureCount}
												</Typography>
											</Grid>
										</Grid>
										<Grid container>
											<Grid item width={'120px'} marginRight='16px'>
												<Typography variant='body2' color='common.neutral40'>
													{t('user.newTreasureNum')}
												</Typography>
											</Grid>
											<Grid item xs>
												<Typography
													variant='body1'
													sx={{ wordBreak: 'break-word' }}
												>
													{Number(watchUserTreasureCount) +
														Number(details.userTreasureCount) || 0}
												</Typography>
											</Grid>
										</Grid>
									</Box>
									<AppNumberTextField
										control={methods.control}
										name='num'
										fieldLabel={t('user.changeTreasure')}
										type='number'
										size='small'
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ChangeTreasureTotalModal;
